/**
 * Laravel validation on `csv_file` (wrong type, missing file, too large).
 * Check before {@link parseTradieCsvImport422} when handling import errors.
 *
 * @param {import('axios').AxiosResponse | null | undefined} response - axios error.response
 * @returns {string | null} First validation message, or null
 */
export function getTradieImportCsvFileValidationMessage(response) {
  if (!response || response.status !== 422 || response.data == null) return null

  const data = mergeCsvImportPayload(response.data)
  const raw = data.errors && data.errors.csv_file
  if (!Array.isArray(raw) || raw.length === 0) return null
  const first = raw[0]
  if (typeof first !== 'string' || !first.trim()) return null
  return first.trim()
}

/**
 * True when `errors.csv_file` is Laravel file/MIME/size rules — show a short toast.
 * False for template/schema/content problems (often still under `csv_file`) — use failure modal.
 */
export function isTradieImportCsvFileMimeValidationMessage(message) {
  if (!message || typeof message !== 'string') return false
  const m = message.trim()
  const lower = m.toLowerCase()
  if (lower.includes('must be a file of type')) return true
  if (lower.includes('must be an image')) return true
  if (/^the (csv |excel )?file must be a file of type/i.test(m)) return true
  if (/^the csv file field is required\.?$/i.test(m)) return true
  if (lower.includes('may not be greater than')) return true
  return false
}

/**
 * Tradie CSV import: HTTP 422 means the file was fully rejected (no partial import).
 * Backend may send:
 * - Legacy: message, emails (already registered)
 * - Company CSV: message, imported_count, imported_emails,
 *   invited_existing_accounts (invite-only existing users), skipped_* lists
 *
 * @param {import('axios').AxiosResponse | null | undefined} response - axios error.response
 * @returns {{
 *   summary: string,
 *   emails: string[],
 *   skippedAlreadyInCompany: string[],
 *   skippedBusinessAdmins: string[],
 *   importedCount: number | null,
 *   importedEmails: string[],
 *   invitedEmails: string[],
 * } | null}
 */
export function parseTradieCsvImport422(response) {
  if (!response || response.status !== 422 || response.data == null) return null

  const data = mergeCsvImportPayload(response.data)
  const summary =
    typeof data.message === 'string' && data.message.trim()
      ? data.message.trim()
      : 'Import was not completed.'

  const emails = normalizeEmailList(data.emails)
  const skippedAlreadyInCompany = normalizeEmailList(
    data.skipped_already_in_company,
  )
  const skippedBusinessAdmins = normalizeEmailList(data.skipped_business_admins)
  const importedEmails = normalizeEmailList(data.imported_emails)
  const invitedEmails = invitedExistingAccountsFromPayload(data)
  const importedCount =
    typeof data.imported_count === 'number' ? data.imported_count : null

  return {
    summary,
    emails,
    skippedAlreadyInCompany,
    skippedBusinessAdmins,
    importedCount,
    importedEmails,
    invitedEmails,
  }
}

/**
 * Company `import-tradie-csv`: 204 = full success; 200 JSON = partial success with skip lists.
 *
 * @param {import('axios').AxiosResponse} res
 * @returns {{ kind: 'full_success' } | { kind: 'partial_success', message: string, importedCount: number, importedEmails: string[], invitedEmails: string[], skippedAlreadyInCompany: string[], skippedBusinessAdmins: string[] }}
 */
export function normalizeCompanyTradieCsvImportResponse(res) {
  if (!res) return { kind: 'full_success' }
  if (res.status === 204) return { kind: 'full_success' }

  if (res.status === 200) {
    const merged = mergeCsvImportPayload(res.data)
    const raw = unwrapCompanyImportBody(merged)
    if (raw && typeof raw === 'object') {
      return {
        kind: 'partial_success',
        message: typeof raw.message === 'string' ? raw.message : '',
        importedCount:
          typeof raw.imported_count === 'number' ? raw.imported_count : 0,
        importedEmails: normalizeEmailList(raw.imported_emails),
        invitedEmails: invitedExistingAccountsFromPayload(raw),
        skippedAlreadyInCompany: normalizeEmailList(
          raw.skipped_already_in_company,
        ),
        skippedBusinessAdmins: normalizeEmailList(raw.skipped_business_admins),
      }
    }
  }

  return { kind: 'full_success' }
}

/**
 * Laravel often nests resource fields under `data`; merge so lists are visible.
 * @param {object} raw
 */
export function mergeCsvImportPayload(raw) {
  if (!raw || typeof raw !== 'object') return raw
  const inner = raw.data
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...inner, ...raw }
  }
  return raw
}

function unwrapCompanyImportBody(data) {
  if (!data || typeof data !== 'object') return null
  if (
    'imported_count' in data ||
    'skipped_already_in_company' in data ||
    'invited_existing_accounts' in data ||
    'invited_emails' in data
  ) {
    return data
  }
  if (
    data.data &&
    typeof data.data === 'object' &&
    ('imported_count' in data.data ||
      'skipped_already_in_company' in data.data ||
      'invited_existing_accounts' in data.data ||
      'invited_emails' in data.data)
  ) {
    return data.data
  }
  return data
}

/** Backend CSV field `invited_existing_accounts`; legacy `invited_emails` supported. */
function invitedExistingAccountsFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return []
  const raw =
    payload.invited_existing_accounts != null
      ? payload.invited_existing_accounts
      : payload.invited_emails
  return normalizeEmailList(raw)
}

function normalizeEmailList(arr) {
  if (!Array.isArray(arr)) return []
  return arr.map((e) => normalizeEmailDisplay(e)).filter(Boolean)
}

function normalizeEmailDisplay(raw) {
  let s = String(raw).trim()
  if (s.startsWith('<') && s.endsWith('>')) {
    s = s.slice(1, -1).trim()
  }
  return s
}
